import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store/index';
import { ref, computed, onMounted } from 'vue';
import { AxiosError } from 'axios';

import { dispatchLogIn } from '@/store/main/actionsMain';
import BaseButtonSubmit from '@/views/base/BaseButtonSubmit.vue';
import BaseButtonText from '@/views/base/BaseButtonText.vue';
import BaseFormCard from '@/views/base/BaseFormCard.vue';
import LoginForm from '@/views/pages/login/LoginForm.vue';

import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { isTrialEnvironment, trialLoginUrl, contractedLoginUrl } from '@/env';
import clerk, { isClerkReady } from '@/lib/clerk';
import router from '@/router';


export default /*#__PURE__*/_defineComponent({
  __name: 'LoginPage',
  setup(__props) {

const m = defineMessages({
  forgotPassword: 'パスワードがわからない場合はこちら',
  logoutSuccessful: 'ログアウトしました',
  tfaAddedMessage: '二要素認証が追加されました。再度ログインしてください。',
  sessionExpired:
    'セッションが期限切れになりました。再度ログインしてください。',
  loginToContinue: 'ログインして続行してください。',
  inactiveUser:
    'アカウント登録が未完了であるためログインができません。「[ケミカン]アカウントの登録完了をお願いします」というメールを開き、アカウント登録を完了してください。',
  samlLogin: 'SAMLログインはこちら',
  freeTrialLogin: '無料トライアルをご利用中の方はこちら',
  noAccountNote: '※アカウントをお持ちでない方は管理者にお問い合わせください',
  loginContracted: 'ログイン（ご契約中のお客様）',
  contractedLogin: 'ご契約中のお客様はこちら',
  processingSsoLogin: 'ログイン処理中です...',
});

const isValidForm = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const email = ref<string>('');
const password = ref<string>('');
const submitErrorKey = ref<string>('');
const submitErrorText = ref<string>('');
const isProcessingClerkTicket = ref<boolean>(false);

// Carry `redirect` so a SAML login can also return to the page (e.g. the
// regulation app) that sent the user here.
const goToSamlLogin = () => {
  const { redirect } = router.currentRoute.query;
  router.push({ name: 'samlLogin', query: redirect ? { redirect } : {} });
};

const hasRedirectQuery = computed(() => {
  const hash = window.location.hash;
  return hash.includes('?redirect') || hash.includes('&redirect');
});

const state = JSON.parse(sessionStorage.getItem('loginState') || '{}');
if (state && Object.keys(state).length > 0) {
  sessionStorage.removeItem('loginState');
}

// Check for SAML user not registered error
const samlError = sessionStorage.getItem('samlUserNotRegistered');
let samlErrorData;
if (samlError) {
  samlErrorData = JSON.parse(samlError);
  sessionStorage.removeItem('samlUserNotRegistered');
}

let displayedMessage, displayedMessageType;

if (samlErrorData) {
  // SAML user not registered error has highest priority
  displayedMessage = samlErrorData.message;
  displayedMessageType = samlErrorData.color;
} else if (state.logout) {
  displayedMessage = m.logoutSuccessful;
  displayedMessageType = 'success';
} else if (state.tfaCompleted) {
  displayedMessage = m.tfaAddedMessage;
  displayedMessageType = 'success';
} else if (state.tokenInvalidated) {
  displayedMessage = m.sessionExpired;
  displayedMessageType = 'warning';
} else if (state.inactiveUser) {
  displayedMessage = m.inactiveUser;
  displayedMessageType = 'warning';
} else if (state.genericError) {
  displayedMessage = sharedMessages.genericError;
  displayedMessageType = 'error';
} else if (hasRedirectQuery.value) {
  displayedMessage = m.loginToContinue;
  displayedMessageType = 'info';
}

const goToTrialLogin = () => {
  window.location.href = trialLoginUrl;
};

const goToContractedLogin = () => {
  window.location.href = contractedLoginUrl;
};

// IdP-Initiated SAML SSO lands here with a one-time sign-in ticket rather than
// through /saml/callback (which only handles the SP-initiated redirect-based
// flow via handleRedirectCallback). The ticket is short-lived (consumed by
// Clerk within ~60s of issuance), so it must be exchanged immediately on
// mount rather than deferred to user interaction.
const consumeClerkTicket = async (): Promise<void> => {
  const params = new URLSearchParams(window.location.search);
  const ticket = params.get('__clerk_ticket');
  const ticketStatus = params.get('__clerk_status');

  if (!ticket || ticketStatus !== 'sign_in') return;

  // Strip the ticket from the URL right away: it's single-use, so it must
  // not linger in browser history or get resubmitted on refresh.
  params.delete('__clerk_ticket');
  params.delete('__clerk_status');
  const remainingQuery = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${
      window.location.hash
    }`
  );

  isProcessingClerkTicket.value = true;
  try {
    await isClerkReady;
    const signIn = await clerk.client.signIn.create({
      strategy: 'ticket',
      ticket,
    });

    if (signIn.status === 'complete') {
      await clerk.setActive({ session: signIn.createdSessionId });
      if (!clerk.session) {
        throw new Error('Clerk session not found');
      }
      // SAML logins may be newly JIT-provisioned and still settling via an
      // async webhook, so route through the same completion/polling page the
      // SP-initiated flow uses instead of assuming the backend is ready yet.
      // Reload instead of router.push: the app was initialized logged-out
      // (company, feature flags, etc. not loaded), so an in-app navigation
      // would leave the next page blank until a manual reload.
      window.location.hash = '/saml/complete';
      window.location.reload();
    } else if (signIn.status === 'needs_second_factor') {
      const tfaStrategy = signIn.supportedSecondFactors?.filter(
        (f) => f.strategy !== 'backup_code'
      )[0]?.strategy;
      if (!tfaStrategy) {
        throw new Error('Clerk 2FA strategy not found');
      }

      const phoneNumberFactor = signIn?.supportedSecondFactors?.find(
        (sf) => sf.strategy === 'phone_code'
      );
      const phoneNumberId = phoneNumberFactor?.phoneNumberId;
      if (tfaStrategy === 'phone_code' && phoneNumberId) {
        await signIn.prepareSecondFactor({
          strategy: 'phone_code',
          phoneNumberId,
        });
      }

      const emailCodeFactor = signIn?.supportedSecondFactors?.find(
        (sf) => (sf.strategy as string) === 'email_code'
      ) as unknown as { emailAddressId: string } | undefined;
      if ((tfaStrategy as string) === 'email_code' && emailCodeFactor) {
        await signIn.prepareSecondFactor({
          strategy: 'email_code',
          emailAddressId: emailCodeFactor.emailAddressId,
        } as unknown as Parameters<typeof signIn.prepareSecondFactor>[0]);
      }

      await router.push({
        name: 'loginVerify',
        params: { mode: tfaStrategy },
      });
    } else {
      throw new Error('Clerk SAML ticket sign-in failed');
    }
  } catch (e) {
    await router.push({ name: 'samlLoginError' });
  } finally {
    isProcessingClerkTicket.value = false;
  }
};

onMounted(() => {
  void consumeClerkTicket();
});

const submit = async (): Promise<void> => {
  if (isLoading.value || !isValidForm.value) return;

  isLoading.value = true;
  submitErrorText.value = '';
  submitErrorKey.value = '';

  try {
    const data = {
      username: email.value,
      password: password.value,
    };

    const res = await dispatchLogIn(store, data);
    if (res?.error) {
      submitErrorText.value = res.messageText;
    }
  } catch (e) {
    if (e instanceof AxiosError && e.response?.data?.detail) {
      submitErrorText.value = e.response.data.detail;
    } else {
      submitErrorKey.value = sharedMessages.genericError;
    }
  } finally {
    isLoading.value = false;
  }
};

return { __sfc: true,m, isValidForm, isLoading, email, password, submitErrorKey, submitErrorText, isProcessingClerkTicket, goToSamlLogin, hasRedirectQuery, state, samlError, samlErrorData, displayedMessage, displayedMessageType, goToTrialLogin, goToContractedLogin, consumeClerkTicket, submit, BaseButtonSubmit, BaseButtonText, BaseFormCard, LoginForm, sharedMessages, isTrialEnvironment }
}

})